/**
 * Runtime detection + enablement policy (spec 68 §4.3, §9.3, §11.1).
 *
 * The facade itself is runtime-neutral and defaults to a noop sink; these
 * helpers let the boot functions (bootNode / bootWorker / bootBrowser)
 * decide whether to mount a real adapter or stay silent.
 */

export type Runtime = 'node' | 'worker' | 'browser';

export type DeploymentEnv = 'prod' | 'dev';

/** Debug surfaces opted in via `DEBUG_TELEMETRY` (§11.2). */
export type DebugSurface = 'console' | 'dash0' | 'amplitude';

interface GlobalProbe {
  window?: unknown;
  WorkerGlobalScope?: unknown;
  // Cloudflare Workers expose `navigator.userAgent === 'Cloudflare-Workers'`.
  navigator?: { userAgent?: string };
}

/**
 * Detect the current runtime. Browser wins first (has `window`); a
 * Cloudflare Worker exposes `WorkerGlobalScope` or the Workers UA; Node is
 * the fallback.
 */
export function detectRuntime(g: GlobalProbe = globalThis as unknown as GlobalProbe): Runtime {
  if (typeof g.window !== 'undefined') return 'browser';
  if (typeof g.WorkerGlobalScope !== 'undefined') return 'worker';
  if (g.navigator?.userAgent === 'Cloudflare-Workers') return 'worker';
  return 'node';
}

/** Read `DEPLOYMENT_ENV` (falls back to NODE_ENV heuristic). */
export function deploymentEnv(env: Record<string, string | undefined> = process.env): DeploymentEnv {
  const raw = env.DEPLOYMENT_ENV;
  if (raw === 'prod' || raw === 'dev') return raw;
  return env.NODE_ENV === 'production' ? 'prod' : 'dev';
}

/** Parse the comma-separated `DEBUG_TELEMETRY` opt-in list (§11.2). */
export function debugSurfaces(env: Record<string, string | undefined> = process.env): Set<DebugSurface> {
  const raw = env.DEBUG_TELEMETRY;
  if (!raw) return new Set();
  const out = new Set<DebugSurface>();
  for (const token of raw.split(',')) {
    const t = token.trim();
    if (t === 'console' || t === 'dash0' || t === 'amplitude' || t === '1') {
      // `DEBUG_TELEMETRY=1` is a shorthand for "console" (§2 goals).
      out.add(t === '1' ? 'console' : t);
    }
  }
  return out;
}

/**
 * Is telemetry live? Dev is silent by default (§11.1); it turns on only
 * when a `DEBUG_TELEMETRY` surface is opted in. prod is always
 * live (subject to the individual adapter having an endpoint/token).
 */
export function isTelemetryEnabled(env: Record<string, string | undefined> = process.env): boolean {
  const tier = deploymentEnv(env);
  if (tier === 'prod') return true;
  return debugSurfaces(env).size > 0;
}
